import React from "react";
import { TextField, Popover, Box, IconButton } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar, PickersDay } from "@mui/x-date-pickers";
import {
  isBefore,
  isAfter,
  isSameDay,
  addMonths,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { ArrowForward, ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import i18n from "../../i18n";
import moment from "moment";

function CustomPickersDay(props) {
  const { day, outsideCurrentMonth, ...other } = props;
  const { startDate, endDate } = props;

  const selectedStart = startDate && isSameDay(day, startDate);
  const selectedEnd = endDate && isSameDay(day, endDate);
  const inRange =
    startDate && endDate && isAfter(day, startDate) && isBefore(day, endDate);

  const isHoverInRange =
    startDate &&
    props.hoverDate &&
    !endDate &&
    ((isAfter(day, startDate) && isBefore(day, props.hoverDate)) ||
      (isAfter(day, props.hoverDate) && isBefore(day, startDate)));

  let bgColor;
  let textColor;

  if (selectedStart || selectedEnd) {
    bgColor = "#1976d2";
    textColor = "#fff";
  } else if (inRange || isHoverInRange) {
    bgColor = "#1976d21f";
    textColor = "#000";
  } else {
    bgColor = undefined;
    textColor = undefined;
  }

  return (
    <PickersDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      sx={{
        backgroundColor: bgColor || "transparent",
        color: textColor || "#000",
        "&:hover": {
          backgroundColor: bgColor || "rgba(0, 0, 0, 0.04)",
        },
        "&.Mui-selected": {
          backgroundColor: bgColor || "transparent",
          color: textColor || "inherit",
          borderRadius: "20px",
          "&:hover": {
            backgroundColor: bgColor || "rgba(0, 0, 0, 0.04)",
          },
        },
      }}
    />
  );
}

class CustomDateRangePicker extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      anchorEl: null,
      startDate: null,
      endDate: null,
      hoverDate: null,
      currentMonth: new Date(),
    };
  }
  setStateAsync(state) {
    return new Promise((resolve) => {
      this.setState(state, resolve);
    });
  }
  handleOpen = (event) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleClose = () => {
    this.setState({ anchorEl: null });
  };

  handleSelectDate = async (date) => {
    const { startDate, endDate } = this.state;
    const dateFormat = 'YYYY-MM-DD'; // BE desired format
  
    if (!startDate || (startDate && endDate)) {
      await this.setStateAsync({ startDate: date, endDate: null });
    } else if (isBefore(date, startDate)) {
      await this.setStateAsync({ startDate: date });
    } else {
      await this.setStateAsync({ endDate: date });
    }
  
    if (this.state?.startDate && this.state?.endDate) {
      const formattedStart = moment(this.state.startDate).format(dateFormat);
      const formattedEnd = moment(this.state.endDate).format(dateFormat);
      this.props?.onChange([formattedStart, formattedEnd]);
    }
  };
  

  handleHoverDate = (date) => {
    this.setState({ hoverDate: date });
  };

  formatRange = () => {
    const { startDate, endDate } = this.state;
    const dateFormat = i18n.t("OPTIONS.date_format");
  
    if (startDate && endDate) {
      return `${moment(startDate).format(dateFormat)} – ${moment(endDate).format(dateFormat)}`;
    } else if (startDate) {
      return `${moment(startDate).format(dateFormat)} –`;
    } else {
      return "";
    }
  };
  

  changeMonth = (offset) => {
    const { currentMonth } = this.state;
    this.setState({
      currentMonth: addMonths(currentMonth, offset),
    });
  };

  handleClearSelection = async () => {
    await this.setStateAsync({
      startDate: null,
      endDate: null,
      hoverDate: null,
    });
    this.props?.onChange && this.props.onChange(["", ""]);
  };

  render() {
    const { anchorEl, currentMonth, startDate, endDate, hoverDate } =
      this.state;
    const open = Boolean(anchorEl);
    const id = open ? "date-range-popover" : undefined;

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <TextField
          label={this.props?.label || i18n.t("select_date_range")}
          value={this.formatRange()}
          onClick={this.handleOpen}
          fullWidth
          readOnly
          InputProps={{
            endAdornment: (this.state.startDate || this.state.endDate) && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  this.handleClearSelection();
                }}
                size="small"
              >
                ✕
              </IconButton>
            ),
          }}
        />
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={this.handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, p: 2, alignItems: "center" }}>
            <IconButton onClick={() => this.changeMonth(-1)}>
              <ArrowBack />
            </IconButton>

            {[0, 1].map((monthOffset) => (
              <DateCalendar
                key={monthOffset}
                value={addMonths(currentMonth, monthOffset)}
                onChange={this.handleSelectDate}
                onMouseLeave={() => this.handleHoverDate(null)}
                slots={{
                  day: CustomPickersDay,
                  leftArrowIcon: () => <></>,
                  rightArrowIcon: () => <></>,
                }}
                slotProps={{
                  day: {
                    startDate,
                    endDate,
                    hoverDate,
                    onMouseEnter: this.handleHoverDate,
                  },
                }}
                minDate={startOfMonth(addMonths(currentMonth, monthOffset))}
                maxDate={endOfMonth(addMonths(currentMonth, monthOffset))}
              />
            ))}

            <IconButton onClick={() => this.changeMonth(1)}>
              <ArrowForward />
            </IconButton>
          </Box>
        </Popover>
      </LocalizationProvider>
    );
  }
}

export default function (props) {
  const navigate = useNavigate();
  const params = useParams();
  return (
    <CustomDateRangePicker {...props} navigate={navigate} params={params} />
  );
}
